import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Form, Modal, Badge } from "react-bootstrap";
import {
  ReplyFill,
  XCircleFill,
  TicketDetailed,
  PersonCircle,
  EnvelopeFill,
  GeoAltFill,
  ClockHistory,
  CheckCircleFill,
  SendFill,
  ChatDots,
} from "react-bootstrap-icons";
import {
  Spinner,
  Alert
} from 'react-bootstrap';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
  Paper,
  Avatar,
  Chip,
} from "@mui/material";
import API_BASE_URL from "../../../config/api";
import { getAdminAuthHeader, openAuthenticatedFile } from "../../../services/auth";
import AuthenticatedAvatar from "../../../components/AuthenticatedAvatar";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{
          color,
          lineHeight: 1,
          opacity: disabled ? 0.4 : 1,
          minWidth: "auto",
        }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

const Tickets = () => {
  const [activeTicket, setActiveTicket] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [tickets, setTickets] = useState([]);
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const conversationRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const prevThreadLength = useRef(0);
  const [showNewMsgTooltip, setShowNewMsgTooltip] = useState(false);
  const [newMsgIndex, setNewMsgIndex] = useState(null);
  const [lastThreadLength, setLastThreadLength] = useState(0);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/support/tickets`, {
      headers: getAdminAuthHeader(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setTickets(
            data.data.map((ticket, idx) => ({
              id: idx + 1,
              ticketId: ticket.ticketId,
              subject: ticket.subject,
              name: ticket.user?.name || "",
              email: ticket.user?.email || "",
              userType: ticket.user?.role || "",
              location: ticket.user?.location || "",
              status: ticket.status,
              profilePicture:
                ticket.user?.profilePicture ||
                `https://img.freepik.com/free-vector/businessman-character-avatar-isolated_24877-60111.jpg`,
              messages: ticket.messages || [],
            })),
          );
        }
      });
  }, []);

  // Detect user scroll
  useEffect(() => {
    const ref = conversationRef.current;
    if (!ref) return;
    const handleScroll = () => {
      if (ref.scrollHeight - ref.scrollTop - ref.clientHeight > 10) {
        setIsUserScrolling(true);
      } else {
        setIsUserScrolling(false);
      }
    };
    ref.addEventListener("scroll", handleScroll);
    return () => ref.removeEventListener("scroll", handleScroll);
  }, [activeTicket]);

  // Poll for new messages
  useEffect(() => {
    let interval;
    if (activeTicket) {
      interval = setInterval(() => {
        fetch(`${API_BASE_URL}/api/support/thread/${activeTicket.ticketId}`, {
          headers: getAdminAuthHeader(),
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.success && Array.isArray(data.thread)) {
              setThread(data.thread);
            }
          });
      }, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTicket]);

  // Helper to check if user is at bottom
  const isAtBottom = () => {
    const ref = conversationRef.current;
    if (!ref) return false;
    return ref.scrollHeight - ref.scrollTop - ref.clientHeight <= 10;
  };

  // Always scroll to bottom when modal opens (activeTicket changes)
  useEffect(() => {
    if (conversationRef.current && activeTicket) {
      setTimeout(() => {
        conversationRef.current.scrollTop =
          conversationRef.current.scrollHeight;
      }, 0);
    }
  }, [activeTicket]);

  // Scroll to bottom only if new message comes and user is not scrolling
  useEffect(() => {
    if (conversationRef.current && thread.length > prevThreadLength.current) {
      const lastMsg = thread[thread.length - 1];
      const isAdminMsg =
        lastMsg && lastMsg.sender && lastMsg.sender.role === "admin";

      if (isAtBottom() || isAdminMsg) {
        conversationRef.current.scrollTop =
          conversationRef.current.scrollHeight;
        setShowNewMsgTooltip(false);
        setNewMsgIndex(null);
      } else {
        setShowNewMsgTooltip(
          lastMsg && lastMsg.sender && lastMsg.sender.role !== "admin",
        );
        setNewMsgIndex(thread.length - 1);
      }
    }
    prevThreadLength.current = thread.length;
    setLastThreadLength(thread.length);
  }, [thread, activeTicket]);

  // Hide tooltip if user scrolls to bottom
  useEffect(() => {
    if (isAtBottom()) {
      setShowNewMsgTooltip(false);
      setNewMsgIndex(null);
    }
  }, [isUserScrolling, thread]);

  const handleReply = async (ticketId) => {
    if (!replyText.trim()) return;

    const formData = new FormData();
    formData.append("ticketId", activeTicket.ticketId);
    formData.append("message", replyText);
    formData.append("isAdmin", true);

    await fetch(
      `${API_BASE_URL}/api/support/message/${activeTicket.ticketId}`,
      {
        method: "POST",
        headers: getAdminAuthHeader(),
        body: formData,
      },
    );

    setReplyText("");
    openTicketThread(activeTicket);
  };

  const closeTicket = (ticketId) => {
    const updatedTickets = tickets.map((ticket) => {
      if (ticket.id === ticketId) {
        return { ...ticket, status: "Closed" };
      }
      return ticket;
    });

    setTickets(updatedTickets);
    setActiveTicket(null);
  };

  const handleCloseTicket = async (ticketId) => {
    if (!ticketId) return alert("Invalid ticket ID");

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/support/close/${activeTicket.ticketId}/Closed`,
        {
          method: "PUT",
          headers: {
            ...getAdminAuthHeader(),
            "Content-Type": "application/json",
          },
        },
      );

      const data = await res.json();

      if (res.ok && data.success) {
        setActiveTicket((prev) => ({
          ...prev,
          status: "Closed",
        }));
        alert("Ticket closed successfully");
      } else {
        alert(data.message || "Failed to close ticket");
      }
    } catch (error) {
      console.error("Error closing ticket:", error);
      alert("Something went wrong while closing the ticket");
    }
  };

  const openTicketThread = (ticket) => {
    setLoadingThread(true);
    fetch(`${API_BASE_URL}/api/support/thread/${ticket.ticketId}`, {
      headers: getAdminAuthHeader(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.thread)) {
          setThread(data.thread);
        } else {
          setThread([]);
        }
        setActiveTicket(ticket);
        setLoadingThread(false);
      })
      .catch(() => {
        setThread([]);
        setActiveTicket(ticket);
        setLoadingThread(false);
      });
  };

  const handleNewMsgClick = () => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
    setShowNewMsgTooltip(false);
    setNewMsgIndex(null);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else {
      return date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Link underline="hover" color="inherit" href="/help-support">
          Help & Support
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Support Tickets
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box display="flex" alignItems="center" mb={3} gap={1.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "11px",
            background: primaryGradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
          }}
        >
          <TicketDetailed size={20} color="#fff" />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
          >
            Support Tickets
          </Typography>
          <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
            Manage and respond to user support requests
          </Typography>
        </Box>
      </Box>

      {/* Tickets Table */}
      <Paper
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
          mb: 3,
        }}
      >
        <Table hover className="mb-0 align-middle">
          <thead style={{ background: primaryGradient }}>
            <tr>
              {[
                "Sr.no",
                "From",
                "Subject",
                "User Role",
                "Location",
                "Status",
                "Action",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 700,
                    fontSize: "0.72rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    padding: "13px 16px",
                    border: "none",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-5 text-muted">
                  No tickets found.
                </td>
              </tr>
            ) : (
              tickets.map((ticket, index) => (
                <tr
                  key={ticket.id}
                  style={{ borderBottom: "1px solid #f0f4ff" }}
                >
                  <td
                    style={{
                      color: "#aab4cc",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {index + 1}
                  </td>
                  <td>
                    <Box display="flex" alignItems="center" gap={2}>
                      <AuthenticatedAvatar
                        src={ticket.profilePicture}
                        alt={ticket.name}
                        sx={{
                          width: 40,
                          height: 40,
                          border: "2px solid #e4eaf8",
                        }}
                      />
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1a237e",
                            display: "block",
                          }}
                        >
                          {ticket.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.65rem",
                            color: "#7b8ab8",
                            display: "block",
                          }}
                        >
                          {ticket.email}
                        </Typography>
                      </Box>
                    </Box>
                  </td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                    {ticket.subject}
                  </td>
                  <td>
                    <Chip
                      label={ticket.userType}
                      size="small"
                      sx={{
                        backgroundColor: "#e4eaf8",
                        color: "#1a237e",
                        fontSize: "0.7rem",
                        height: "24px",
                      }}
                    />
                  </td>
                  <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                    <GeoAltFill size={12} className="me-1" />
                    {ticket.location || "N/A"}
                  </td>
                  <td>
                    <span
                      className={`badge px-3 py-2 rounded-pill ${
                        ticket.status === "Open"
                          ? "bg-warning text-dark"
                          : "bg-success"
                      }`}
                      style={{ fontSize: "0.75rem" }}
                    >
                      {ticket.status}
                    </span>
                  </td>
                  <td>
                    <ActionBtn
                      tooltip="Reply to Ticket"
                      color="#0d47a1"
                      onClick={() => openTicketThread(ticket)}
                    >
                      <ReplyFill size={16} />
                    </ActionBtn>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Paper>

      {/* Ticket Conversation Modal */}
      <Modal
        show={!!activeTicket}
        onHide={() => {
          setActiveTicket(null);
          setThread([]);
        }}
        size="lg"
        centered
        style={{ zIndex: 1050 }}
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <ChatDots size={18} />
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="caption"
                sx={{ color: "#fff", fontWeight: 600 }}
              >
                {activeTicket?.subject}
              </Typography>
              <span
                className={`badge px-2 py-1 rounded-pill ${
                  activeTicket?.status === "Open"
                    ? "bg-warning text-dark"
                    : "bg-success"
                }`}
                style={{ fontSize: "0.65rem" }}
              >
                {activeTicket?.status}
              </span>
            </Box>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: 0 }}>
          <Box
            sx={{
              height: "400px",
              overflowY: "auto",
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              background: "#f0f4ff",
            }}
            ref={conversationRef}
          >
            {loadingThread ? (
              <Box display="flex" justifyContent="center" py={3}>
                <Spinner animation="border" style={{ color: "#0d47a1" }} />
              </Box>
            ) : thread.length > 0 ? (
              thread.map((msg, index) => {
                const isAdmin = msg.sender?.role === "admin";
                const isInstructor = msg.sender?.role === "instructor";
                const isUser = msg.sender?.role === "user";

                let avatarColor = "#0d47a1";
                let roleText = "";

                if (isAdmin) {
                  avatarColor = "#c62828";
                  roleText = "Admin";
                } else if (isInstructor) {
                  avatarColor = "#2e7d32";
                  roleText = "Instructor";
                } else if (isUser) {
                  avatarColor = "#f57f17";
                  roleText = "User";
                }

                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: isAdmin ? "flex-end" : "flex-start",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isAdmin ? "row-reverse" : "row",
                        alignItems: "flex-end",
                        gap: 1,
                        maxWidth: "80%",
                      }}
                    >
                      <AuthenticatedAvatar
                        // Admin messages should always use the text fallback instead
                        // of displaying the broken/default profile image.
                        src={isAdmin ? undefined : msg.sender?.profilePicture}
                        alt={msg.sender?.name}
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: avatarColor,
                          border: "2px solid #fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        {msg.sender?.name?.charAt(0)?.toUpperCase() || "?"}
                      </AuthenticatedAvatar>

                      <Paper
                        sx={{
                          p: 1.5,
                          backgroundColor: isAdmin ? "#dcf8c6" : "#fff",
                          borderRadius: isAdmin
                            ? "12px 12px 4px 12px"
                            : "12px 12px 12px 4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          border: "1px solid #e4eaf8",
                        }}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          mb={0.5}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: isAdmin ? "#1a237e" : "#0d47a1",
                              fontSize: "0.75rem",
                            }}
                          >
                            {msg.sender?.name || "Unknown"}
                          </Typography>
                          <Chip
                            label={roleText}
                            size="small"
                            sx={{
                              height: "18px",
                              fontSize: "0.6rem",
                              backgroundColor: isAdmin
                                ? "rgba(198,40,40,0.1)"
                                : "rgba(13,71,161,0.1)",
                              color: isAdmin ? "#c62828" : "#0d47a1",
                            }}
                          />
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#1a237e",
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.message}
                        </Typography>

                        {msg.attachment && (
                          <Box mt={1}>
                            <button
                              type="button"
                              onClick={() =>
                                openAuthenticatedFile(msg.attachment).catch((error) =>
                                  alert(error.message || "Unable to open this attachment.")
                                )
                              }
                              style={{
                                border: 0,
                                padding: 0,
                                background: "transparent",
                                cursor: "pointer",
                              }}
                              title="Open attachment"
                            >
                              <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/logo.jpg`}
                                alt="Open attachment"
                                sx={{
                                  width: 24,
                                  height: 24,
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  border: "1px solid #e4eaf8",
                                  display: "block",
                                }}
                              />
                            </button>
                          </Box>
                        )}

                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-end"
                          gap={0.5}
                          mt={0.5}
                        >
                          <ClockHistory
                            size={10}
                            style={{ color: "#7b8ab8" }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.6rem",
                              color: "#7b8ab8",
                            }}
                          >
                            {formatTime(msg.createdAt)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <Box textAlign="center" py={3}>
                <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                  No messages found.
                </Typography>
              </Box>
            )}
          </Box>

          {/* New Message Tooltip */}
          {showNewMsgTooltip && (
            <Box
              sx={{
                position: "fixed",
                bottom: 100,
                right: 40,
                zIndex: 9999,
                cursor: "pointer",
              }}
              onClick={handleNewMsgClick}
            >
              <Paper
                sx={{
                  background: primaryGradient,
                  color: "#fff",
                  px: 2,
                  py: 1,
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
                }}
              >
                <ChatDots size={14} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  New Message
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Reply Section */}
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid #e4eaf8",
              background: "#fff",
            }}
          >
            {activeTicket?.status === "Open" ? (
              <Box>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your reply here..."
                  style={fieldStyle}
                />
                <Box display="flex" justifyContent="space-between" mt={2}>
                  <Button
                    variant="danger"
                    onClick={() => handleCloseTicket(activeTicket?.id)}
                    style={{ borderRadius: "8px" }}
                  >
                    <XCircleFill className="me-2" />
                    Close Ticket
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleReply(activeTicket?.id)}
                    style={{
                      background: primaryGradient,
                      border: "none",
                      borderRadius: "8px",
                    }}
                  >
                    <SendFill className="me-2" />
                    Send Reply
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  background: "linear-gradient(135deg, #2e7d32, #1b5e20)",
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  color: "#fff",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    background: "rgba(255,255,255,0.2)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1,
                  }}
                >
                  <CheckCircleFill size={28} />
                </Box>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  Ticket Closed Successfully
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  No further replies are allowed.
                </Typography>
              </Box>
            )}
          </Box>
        </Modal.Body>
      </Modal>
    </Box>
  );
};

export default Tickets;
